import React, {useEffect, useRef, useState} from "react";
import {Button, ControlLabel, FormControl, FormGroup} from "react-bootstrap";
import locale from "locale";
import Tooltip from "../../Tooltip";
import ImageCropper from "./ImageCropper";

const FILE_TYPE_SVG = 'image/svg+xml';

const CPSettingUploadImage = ({
    title,
    settingName,
    eventImage,
    tooltipText = '',
    availableTypes,
    maxImageHeight = '200px',
    hasCropper = true,
    fixtedCropper = false,
    cropperWidth,
    cropperHeight,
    onUpdate,
}) => {
    const [imageFile, setImageFile] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [imageSrc, setImageSrc] = useState('');
    const [isImageSaved, setImageSaved] = useState(false);
    const [isEditorVisible, setEditorVisible] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        setImageSrc(eventImage ?? '');
    }, [eventImage]);

    const handleDeleteLogo = () => {
        if (!imageSrc) return;

        const allow = confirm(locale('settings.confirmImageRemove'));

        if (!allow) return;

        setImageFile(null);
        setImageSrc('');
        setImageSaved(false);
        onUpdate({[settingName]: "DELETE"});
    };

    const handleUploadFile = (file) => {
        if (!file) return;

        if (!availableTypes[file.type]) {
            setError(locale('settings.fileTypeNotMatch'));
            return;
        }

        setImageFile(file);
        setFileType(file.type);
        setError('');

        if (file.type === FILE_TYPE_SVG || !hasCropper) {
            onUpdate({[settingName]: file});
            setImageSaved(true);

            return;
        }

        setEditorVisible(true);
        setImageSrc(URL.createObjectURL(file));
    };

    const handleCancelSave = () => {
        if (!isImageSaved) {
            setImageFile(null);
            setImageSrc(eventImage || '');
        }

        setEditorVisible(false);
    };

    const handleSave = async (url) => {
        const base64Response = await fetch(url);
        const blob = await base64Response.blob();
        const file = new File(
            [blob],
            `fileName.${availableTypes[fileType]}`,
            {
              type: fileType,
              lastModified: new Date(),
            }
        );

        onUpdate({[settingName]: file});
        setImageSrc(URL.createObjectURL(file));

        setEditorVisible(false);
        setImageSaved(true);
        setError('');
    };

    return (
        <FormGroup>
            <ControlLabel>
                <span>
                    {title}
                    {
                        tooltipText &&
                        <Tooltip width="200px">
                            {tooltipText}
                        </Tooltip>
                    }
                </span>
            </ControlLabel>
            {
                (imageSrc || (isImageSaved && fileType === FILE_TYPE_SVG)) &&
                <FormGroup>
                    <img
                        src={imageSrc}
                        alt=""
                        style={{maxWidth: '100%', maxHeight: maxImageHeight, height: '100%'}}
                    />
                </FormGroup>
            }
            {
                error &&
                <p style={{color: 'red'}}>
                    <i>
                        {error}
                    </i>
                </p>
            }
            <FormGroup>
                <FormControl
                    inputRef={ref => fileInputRef.current = ref}
                    type="file"
                    onChange={(e) => {
                        setError('');
                        handleUploadFile(e.target.files[0])
                    }}
                    style={{display: 'none'}}
                />
                <Button onClick={() => fileInputRef?.current?.click()}>
                    {locale(imageFile || imageSrc
                        ? 'settings.replace'
                        : 'email.upload'
                    )}
                </Button>
                <Button
                    style={{marginLeft: '12px'}}
                    onClick={handleDeleteLogo}
                >
                    {locale('email.delete')}
                </Button>
                {
                    (isImageSaved && fileType !== FILE_TYPE_SVG && hasCropper) &&
                    <Button
                        style={{marginLeft: '12px'}}
                        onClick={() => setEditorVisible(true)}
                    >
                        {locale('email.edit')}
                    </Button>
                }
            </FormGroup>

            <ImageCropper
                isModalVisible={isEditorVisible}
                imageUrl={imageSrc}
                fixtedCropper={fixtedCropper}
                cropperWidth={cropperWidth}
                cropperHeight={cropperHeight}
                onSave={handleSave}
                onClose={handleCancelSave}
            />
        </FormGroup>
    )
};

export default CPSettingUploadImage;
